import type {RouteLocationNormalizedLoaded, RouteLocationRaw, RouteRecordRaw} from 'vue-router';
import {SystemMessageType} from '@myparcel-frontend/types';
import {ordersOverviewGuard} from './guards/ordersOverview-guard';
import {buildOrdersOverviewProps} from './props/buildOrdersOverviewProps';
import useFeatures from '@/composables/useFeatures';
import {useOrderVersion} from '@/composables/useOrderVersion/useOrderVersion';
import {useServiceAccount} from '@/composables/useServiceAccount/useServiceAccount';

const LEGACY_ORDERS_COOKIE = 'orders-legacy=true; path=/; max-age=60; SameSite=Strict';
const CLEAR_LEGACY_ORDERS_COOKIE = 'orders-legacy=; path=/; max-age=0; SameSite=Strict';

export const resolveOrderRoute = (
  onOrderManagement: () => RouteLocationRaw | boolean,
  legacyRedirectPath = '/orders',
): RouteLocationRaw | boolean => {
  const {hasOrderManagement, hasLegacyOrderManagement} = useOrderVersion();
  if (hasOrderManagement) {
    document.cookie = CLEAR_LEGACY_ORDERS_COOKIE;
    return onOrderManagement();
  }
  if (hasLegacyOrderManagement) {
    document.cookie = LEGACY_ORDERS_COOKIE;

    // legacyRedirectPath may carry its own query; current-location params win (so ?uuid= carries over).
    const target = new URL(legacyRedirectPath, window.location.origin);
    new URLSearchParams(window.location.search).forEach((value, key) => {
      target.searchParams.set(key, value);
    });

    window.location.replace(target.toString());
    return false;
  }
  return {name: 'dashboard'};
};

export const orderRoutes: RouteRecordRaw[] = [
  {
    name: 'orders',
    path: '/orders',
    children: [
      {
        name: 'orders-overview',
        path: '',
        component: () => {
          const {isFeatureActive} = useFeatures();
          if (isFeatureActive('page.orders.mobile')) {
            return import('@/views/Orders/OverviewMobile/OverviewMobile.vue');
          }
          return import('@/views/Orders/Overview/Overview.vue');
        },
        meta: {
          title: (t) => t('orders.overview'),
        },
        beforeEnter: (to) => resolveOrderRoute(() => ordersOverviewGuard(to)),
        props: buildOrdersOverviewProps,
      },
      {
        name: 'orders-create-shipment',
        path: ':id/create-shipment',
        props: true,
        component: () => import('@/views/Orders/CreateShipment/CreateShipment.vue'),
        meta: {
          title: (t) => t('orders.newShipment'),
          systemMessageType: SystemMessageType.SHIPMENT_FORM,
        },
        beforeEnter: () => resolveOrderRoute(() => true),
      },
      {
        name: 'order-package-edit',
        path: ':orderId/package/:packageId',
        props: true,
        component: () => import('@/views/Orders/Packages/Edit/Edit.vue'),
        beforeEnter: [() => resolveOrderRoute(() => true)],
        meta: {
          permissions: ['updateShipment'],
          title: (t) => t('shipment.edit'),
          systemMessageType: SystemMessageType.SHIPMENT_FORM,
        },
      },
      {
        name: 'orders-csv-import',
        path: 'csv-import',
        component: () => import('@/views/Orders/OrdersCsvImport/OrdersCsvImport.vue'),
        beforeEnter: () => resolveOrderRoute(() => true),
        meta: {
          title: (t) => t('ui.fileUpload.importCsv'),
          feature: 'page.orders.csvImport',
        },
      },
      {
        name: 'orders-import-overview',
        path: 'import',
        component: () => import('@/views/Orders/OrdersImport/OrdersImport.vue'),
        beforeEnter: () =>
          resolveOrderRoute(() => {
            const {isSalesChannelsAccessible} = useServiceAccount();
            return isSalesChannelsAccessible.value ? true : {name: 'orders-overview'};
          }, '/orders/import'),
        meta: {
          title: (t) => t('orders.order', 2),
        },
        props: (route: RouteLocationNormalizedLoaded) => ({
          query: {
            query: route.query.query,
            filter: {status: ['DISCOVERED']},
          },
          allShops: false,
        }),
      },
    ],
  },
];
